import { useRef, useState } from 'react';
import { Button, Modal, Toast } from '@ds';
import { IShowToast, IToastr } from '@ds/elements/Toast/Toast.types';
import { ButtonSize, ButtonStyle, ButtonVariant } from '@ds/elements/Button/Button.types';
import { useGetUserProfilesImage } from 'src/shared/queries/useGetUserProfilesImage';
import { useUploadUserProfileImage } from 'src/shared/mutations/useUploadUserProfileImage';
import { useHeaderStore } from 'src/stores/HeaderStore';
import { useGetUser } from 'src/shared/queries/useGetUser';
import styles from './UploadProfilePictureModal.module.scss';

const UploadProfilePictureModal: React.FC<{
  displayToast: (details: IShowToast) => void;
}> = ({ displayToast }) => {
  const { data: userInfo } = useGetUser();
  const { data: profileImage } = useGetUserProfilesImage(userInfo?.userId);
  const { mutate: uploadImage, isPending } = useUploadUserProfileImage();
  const { isUploadProfilePictureModalOpen, closeUploadProfilePictureModal } = useHeaderStore();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const toastrRef = useRef<IToastr>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const isEmptyProfileImage = !profileImage || profileImage.includes('assets/Avatar_Temp.png');

  const handleUploadClick = () => {
    const fileInput = fileInputRef.current;

    if (selectedImage && fileInput?.files?.length) {
      onImageUpload(fileInput.files[0]);
    } else if (fileInput) {
      fileInput.value = '';
      fileInput.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files as FileList;

    if (!files) {
      toastrRef.current?.show({ message: 'Please select a file to upload', severity: 'warn' });
      return;
    }

    if (files && files.length > 0 && files[0].size > 2048000) {
      toastrRef.current?.show({ message: 'File size must be under 2MB', severity: 'error' });
      return;
    }

    const fileReader = new FileReader();
    fileReader.onload = () => {
      setSelectedImage(fileReader.result as string);
    };
    fileReader.readAsDataURL(files[0]);

    if (isEmptyProfileImage) {
      onImageUpload(files[0]);
    }
  };

  const onImageUpload = (file: File) => {
    uploadImage(file, {
      onSuccess: () => {
        displayToast({ message: 'Profile picture updated successfully', severity: 'success' });
        onModalClosed();
      },
      onError: () => {
        toastrRef.current?.show({
          message: 'Failed to upload profile picture. Please try again',
          severity: 'error',
        });
      },
      onSettled: () => {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      },
    });
  };

  const onModalClosed = () => {
    closeUploadProfilePictureModal();
    setSelectedImage(null);
    fileInputRef.current!.value = '';
  };

  const renderProfileImage = () => {
    if (selectedImage) {
      return (
        <div className={styles['user-profile-image-container']}>
          <img src={selectedImage} alt="Selected Profile" />
        </div>
      );
    }

    if (isEmptyProfileImage) {
      return (
        <div className={styles['user-dummy-profile-image-container']} onClick={handleUploadClick}>
          <div className={styles['change-profile-image-icon']} />
        </div>
      );
    }

    return (
      <div className={styles['user-profile-image-container']}>
        <img src={profileImage} alt="Profile" />
      </div>
    );
  };

  const getButtonText = () => {
    if (isPending) return 'Uploading...';
    if (selectedImage) return 'Save New Picture';
    return isEmptyProfileImage ? 'Upload' : 'Upload New';
  };

  if (!isUploadProfilePictureModalOpen) return null;

  return (
    <Modal
      visible={isUploadProfilePictureModalOpen}
      onModalClosed={onModalClosed}
      className={styles['upload-profile-picture-modal']}
      headerClass={styles['upload-profile-picture-modal-header']}
      title={'Change Profile Picture'}
      showLoadingOverlay={isPending}
    >
      <div className={styles['upload-profile-picture-modal-content']}>
        <div className="relative">{renderProfileImage()}</div>
        <div className={styles['upload-profile-picture-modal-actions']}>
          <Button disabled={isPending} size={ButtonSize.large} onClick={handleUploadClick}>
            {getButtonText()}
          </Button>
          <Button
            size={ButtonSize.large}
            variant={ButtonVariant.outlined}
            styleType={ButtonStyle.secondary}
            onClick={onModalClosed}
          >
            Cancel
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png, image/jpg, image/jpeg"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      </div>
      <Toast ref={toastrRef} />
    </Modal>
  );
};

export default UploadProfilePictureModal;
